//src/utils/ResizableTitle.tsx
import React, { useRef } from 'react';
import { Resizable } from 'react-resizable';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import 'react-resizable/css/styles.css';
import type { ResizeCallbackData } from 'react-resizable';
type Props = {
  width?: number;
  resizable?: boolean;
  onResize?: (e: React.SyntheticEvent<Element, Event>, data: ResizeCallbackData) => void;
  onAutoFit?: (width: number) => void;

  // DnD (переупорядочивание)
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  isDragging?: boolean;

  // Контекстное меню (Dropdown.menu)
  menu?: MenuProps;

  children?: React.ReactNode;
} & React.HTMLAttributes<HTMLTableCellElement>;

const PADDING_FALLBACK = 24;

const ResizableTitle: React.FC<Props> = ({
  onResize, onAutoFit, width, children, resizable = true,
  draggable, onDragStart, onDragOver, onDrop, onDragEnd, isDragging,
  menu,
  ...rest
}) => {
  const thRef = useRef<HTMLTableCellElement | null>(null);

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const th = thRef.current;
    if (!th) return;
    const table = th.closest('table');
    if (!table) return;

    const idx = th.cellIndex;
    let maxContentWidth = 0;

    // header width
    const titleEl = th.querySelector('.ant-table-column-title') as HTMLElement | null;
    const headWidth = (titleEl?.scrollWidth ?? th.scrollWidth) + PADDING_FALLBACK;
    maxContentWidth = Math.max(maxContentWidth, headWidth);

    // first ~50 rows
    const tbody = table.tBodies?.[0];
    if (tbody) {
      const rows = Array.from(tbody.rows).slice(0, 50);
      for (const r of rows) {
        const cell = r.cells?.[idx] as HTMLElement | undefined;
        if (!cell) continue;
        const w = cell.scrollWidth + PADDING_FALLBACK;
        if (w > maxContentWidth) maxContentWidth = w;
      }
    }
    onAutoFit?.(Math.max(1, maxContentWidth));
  };

  const th = (
    <th
      ref={thRef}
      {...rest}
      className={`${rest.className ?? ''} th-resizable${isDragging ? ' th-dragging' : ''}`}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      onDoubleClick={handleDoubleClick}
    >
      <Dropdown trigger={['contextMenu']} menu={menu ?? { items: [] }}>
        <div className="th-inner">{children}</div>
      </Dropdown>
    </th>
  );

  if (!width || !resizable) return th;

  return (
    <Resizable
      width={width}
      height={0}
      onResize={onResize ?? (() => {})}
      draggableOpts={{ enableUserSelectHack: false }}
      handle={
        <span
          className="col-resize-handle"
          onClick={(e) => e.stopPropagation()}
        />
      }
    >
      {th}
    </Resizable>
  );
};

export default ResizableTitle;


